import { z } from "zod";

export const runnerOutputMimeSchema = z.enum([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/zip",
  "application/epub+zip",
  "application/vnd.amazon.ebook",
  "image/png",
  "image/jpeg",
]);

export const runnerOutputDescriptorSchema = z.object({
  position: z.number().int().min(0).max(499),
  filename: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/),
  mimeType: runnerOutputMimeSchema,
  bytes: z.number().int().positive().max(200 * 1024 * 1024),
  checksumSha256: z.string().regex(/^[a-f0-9]{64}$/),
  pageCount: z.number().int().positive().max(500).optional(),
});

export const runnerPrepareOutputsRequestSchema = z.object({
  outputs: z.array(runnerOutputDescriptorSchema).min(1).max(500)
    .refine((outputs) => new Set(outputs.map((output) => output.position)).size === outputs.length, {
      message: "Output positions must be unique.",
    }),
});

export const runnerCompleteOutputsRequestSchema = z.object({
  outputs: z.array(z.object({
    position: z.number().int().min(0).max(499),
    uploadSessionId: z.uuid(),
  })).min(1).max(500),
});

export const runnerFailureRequestSchema = z.object({
  errorCode: z.string().regex(/^[A-Z0-9_]{3,64}$/),
  retryable: z.boolean(),
  attempt: z.number().int().min(1).max(100),
});

export type RunnerFailureDisposition = "retry" | "ack_failed" | "dead_letter";

export function runnerFailureDisposition(retryable: boolean, attempt: number): RunnerFailureDisposition {
  if (!retryable) return "ack_failed";
  return attempt >= 3 ? "dead_letter" : "retry";
}

export type RunnerOutputDescriptor = z.infer<typeof runnerOutputDescriptorSchema>;
export type RunnerPrepareOutputsRequest = z.infer<typeof runnerPrepareOutputsRequestSchema>;
export type RunnerCompleteOutputsRequest = z.infer<typeof runnerCompleteOutputsRequestSchema>;
export type RunnerFailureRequest = z.infer<typeof runnerFailureRequestSchema>;
